import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getRouteMutationKey } from '.';
import { stopService } from '../../../../application/StopService';
import { Route } from '../../../../domain/route/Route';
import { RouteActionType } from '../context/actions';
import { useRouteContext } from '../context/context';

interface UseDeleteStopsProps {
  route: Route;
}

interface UseDeleteStopsMutationProps {
  stops: string[];
}

type UseDeleteStopResponseProps = ServiceResponse<({ stops }: UseDeleteStopsMutationProps) => void, Route>;

// Hooks act as 'Adapter' layer.
export const useDeleteStops = ({ route }: UseDeleteStopsProps): UseDeleteStopResponseProps => {
  const queryClient = useQueryClient();

  const { routeState, routeDispatch } = useRouteContext();

  const { isLoading, isError, isSuccess, mutate } = useMutation<Route, unknown, UseDeleteStopsMutationProps>(
    ({ stops }: UseDeleteStopsMutationProps) => stopService.deleteStops({ stops }),
    {
      onSuccess: (newRoute) => {
        const key = getRouteMutationKey({ routeId: route.id });
        let previousQueryContent = queryClient.getQueryData<Route>(key);
        if (!previousQueryContent) {
          throw new Error(`previousQueryContent does not exist`);
        }

        previousQueryContent = newRoute;

        queryClient.setQueryData<Route>(key, previousQueryContent);
        routeDispatch({ type: RouteActionType.FETCH_ROUTE_SUCCESS, payload: newRoute });
      },
    }
  );

  return {
    run: mutate,
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    data: routeState.route,
  };
};
